/**
 * Карта пиксельного Лондона: земля по клеткам 16×16, дома, места разговоров и проходимость для поиска пути.
 * Здание — игра (решение владельца 2026-10-22): Хитроу — «Первая ночь», дом с «!» — задание дня, Тауэр — босс,
 * офис под замком — «Глава 2».
 */
export const TILE = 16;
export const MAP_W = 44;
export const MAP_H = 28;

export type Ground = 'grass' | 'pave' | 'road' | 'water' | 'rail' | 'sand' | 'bridge' | 'hedge';

export type BuildingStyle = 'brick' | 'stucco' | 'shop' | 'stone' | 'station' | 'airport' | 'glass' | 'castle';

export interface Building {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  style: BuildingStyle;
  label: string;
  roof: string;
  wall: string;
  /** Этажей на фасаде (рядов клеток), остальное — крыша. */
  floors: number;
  /** Клетка двери от левого края. */
  door: number;
  awning?: [string, string];
  sign?: string;
}

export const BUILDINGS: Building[] = [
  { id: 'museum', x: 14, y: 1, w: 7, h: 7, style: 'stone', label: 'MUSEUM', roof: '#8d97a6', wall: '#e2dacb', floors: 3, door: 3 },
  { id: 'hotel', x: 22, y: 1, w: 5, h: 7, style: 'brick', label: 'HOTEL', roof: '#5a6170', wall: '#a8553f', floors: 3, door: 2 },
  { id: 'hostel', x: 28, y: 2, w: 5, h: 6, style: 'stucco', label: 'HOSTEL', roof: '#8a5a44', wall: '#6fb3a8', floors: 2, door: 2 },
  { id: 'shop', x: 34, y: 3, w: 4, h: 5, style: 'shop', label: 'SHOP', roof: '#b94a3c', wall: '#e9b8c4', floors: 2, door: 1, awning: ['#d6455d', '#fbe9ee'] },
  { id: 'office', x: 39, y: 0, w: 5, h: 8, style: 'glass', label: 'OFFICE', roof: '#3a4150', wall: '#5d7794', floors: 4, door: 2 },
  { id: 'heathrow', x: 0, y: 13, w: 8, h: 6, style: 'airport', label: 'HEATHROW', roof: '#b9bfc8', wall: '#8fb4d6', floors: 2, door: 4 },
  { id: 'station', x: 10, y: 13, w: 7, h: 6, style: 'station', label: 'PADDINGTON', roof: '#9fb4c4', wall: '#c9b79a', floors: 2, door: 3 },
  { id: 'cafe', x: 19, y: 14, w: 4, h: 5, style: 'shop', label: 'CAFE 24', roof: '#3f6b4a', wall: '#efe3cf', floors: 2, door: 1, awning: ['#2f7d5b', '#f1ead8'] },
  { id: 'chippy', x: 24, y: 14, w: 4, h: 5, style: 'shop', label: 'FISH&CHIPS', roof: '#3d5f9e', wall: '#f2f0ea', floors: 2, door: 1, awning: ['#2f5fa8', '#f4f4f4'] },
  { id: 'pharmacy', x: 29, y: 14, w: 4, h: 5, style: 'shop', label: 'PHARMACY', roof: '#7d8590', wall: '#f4f6f6', floors: 2, door: 1, awning: ['#2f9e6a', '#e9f6ef'], sign: 'cross' },
  { id: 'tower', x: 35, y: 13, w: 9, h: 8, style: 'castle', label: '', roof: '#a3a2ab', wall: '#b9b7bf', floors: 3, door: 4 },
];

export interface Place {
  id: string;
  /** Где стоит герой (клетка). */
  hero: [number, number];
  /** Где стоит собеседник: перед дверью, лицом к герою. */
  npc: [number, number];
  building?: string;
}

function doorPlace(b: Building): Place {
  const dx = b.x + b.door;
  return { id: b.id, building: b.id, npc: [dx, b.y + b.h], hero: [dx, b.y + b.h + 1] };
}

export const PLACES: Record<string, Place> = {
  ...Object.fromEntries(BUILDINGS.map((b) => [b.id, doorPlace(b)])),
  street: { id: 'street', npc: [18, 12], hero: [18, 13] },
  busstop: { id: 'busstop', npc: [21, 12], hero: [21, 13] },
  rail: { id: 'rail', npc: [9, 21], hero: [8, 21] },
  park: { id: 'park', npc: [7, 6], hero: [6, 6] },
  start: { id: 'start', npc: [27, 12], hero: [26, 12] },
};

/** Места историй и заданий → место на карте. Телефон — разговор там, где герой стоит. */
export function placeFor(location: string, previous?: string): string {
  const map: Record<string, string> = {
    arrivals: 'heathrow',
    train: 'rail',
    station: 'station',
    street: 'street',
    hotel: 'hotel',
    hostel: 'hostel',
    cafe: 'cafe',
    chippy: 'chippy',
    pharmacy: 'pharmacy',
    shop: 'shop',
    busstop: 'busstop',
    museum: 'museum',
    tower: 'tower',
  };
  if (location === 'phone') return previous && PLACES[previous] ? previous : 'park';
  return map[location] ?? (PLACES[location] ? location : 'start');
}

// ---------------------------------------------------------------------------------------------- земля
export interface Decor {
  kind: 'tree' | 'lamp' | 'bench' | 'phonebox' | 'postbox' | 'busstop' | 'bigben' | 'flowers' | 'bin';
  x: number;
  y: number;
}

export const DECOR: Decor[] = [
  // Гайд-парк
  ...[
    [1, 0],
    [5, 0],
    [9, 1],
    [11, 3],
    [1, 7],
    [10, 7],
    [3, 8],
    [11, 8],
    [0, 4],
  ].map(([x, y]) => ({ kind: 'tree' as const, x, y })),
  { kind: 'bench', x: 4, y: 7 },
  { kind: 'flowers', x: 9, y: 4 },
  { kind: 'flowers', x: 4, y: 5 },
  // улица
  ...[13, 19, 25, 31, 37].map((x) => ({ kind: 'lamp' as const, x, y: 9 })),
  ...[3, 9, 17, 23, 28, 33].map((x) => ({ kind: 'lamp' as const, x, y: 12 })),
  ...[2, 12, 18, 27].map((x) => ({ kind: 'lamp' as const, x, y: 20 })),
  { kind: 'phonebox', x: 16, y: 12 },
  { kind: 'postbox', x: 27, y: 9 },
  { kind: 'busstop', x: 20, y: 12 },
  { kind: 'bin', x: 32, y: 20 },
  { kind: 'bigben', x: 33, y: 21 },
  ...[
    [22, 22],
    [29, 22],
    [41, 23],
    [36, 22],
  ].map(([x, y]) => ({ kind: 'tree' as const, x, y })),
];

const BLOCKING: Decor['kind'][] = ['tree', 'lamp', 'phonebox', 'postbox', 'bigben', 'bin', 'busstop'];

export function buildGround(): Ground[][] {
  const g: Ground[][] = Array.from({ length: MAP_H }, () => Array.from({ length: MAP_W }, () => 'grass' as Ground));
  const fill = (x: number, y: number, w: number, h: number, t: Ground) => {
    for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) if (g[j]?.[i] !== undefined) g[j][i] = t;
  };
  // Гайд-парк: дорожки, пруд, живая изгородь вдоль улицы
  fill(0, 9, 13, 1, 'hedge');
  fill(6, 9, 2, 1, 'sand');
  fill(6, 1, 2, 8, 'sand');
  fill(1, 6, 11, 1, 'sand');
  fill(2, 2, 3, 3, 'water');
  // тротуары и дороги
  fill(13, 8, MAP_W - 13, 2, 'pave');
  fill(0, 10, MAP_W, 2, 'road');
  fill(0, 12, MAP_W, 2, 'pave');
  fill(0, 19, 35, 2, 'pave');
  fill(9, 13, 1, 6, 'pave');
  fill(17, 13, 2, 6, 'pave');
  fill(23, 13, 1, 6, 'pave');
  fill(28, 13, 1, 6, 'pave');
  fill(33, 13, 2, 8, 'pave');
  fill(19, 13, 14, 1, 'pave');
  fill(33, 21, MAP_W - 33, 2, 'pave');
  // железная дорога Хитроу → Паддингтон, набережная, Темза и мост
  fill(0, 21, 18, 1, 'pave');
  fill(0, 22, 18, 1, 'rail');
  fill(0, 23, MAP_W, 1, 'pave');
  fill(0, 24, MAP_W, MAP_H - 24, 'water');
  fill(26, 23, 3, MAP_H - 23, 'bridge');
  return g;
}

/** Проходимость для поиска пути: дома, вода, деревья и столбы — нет. */
export function buildWalkable(ground: Ground[][]): boolean[][] {
  const walk = ground.map((row) => row.map((t) => t !== 'water' && t !== 'hedge' && t !== 'rail'));
  for (const b of BUILDINGS) for (let j = b.y; j < b.y + b.h; j++) for (let i = b.x; i < b.x + b.w; i++) walk[j][i] = false;
  for (const d of DECOR) if (BLOCKING.includes(d.kind)) walk[d.y][d.x] = false;
  // Биг-Бен занимает 2×2 у основания
  const ben = DECOR.find((d) => d.kind === 'bigben');
  if (ben) [0, 1].forEach((dx) => [0, -1].forEach((dy) => (walk[ben.y + dy][ben.x + dx] = false)));
  // через изгородь — калитка на дорожке парка
  walk[9][6] = true;
  walk[9][7] = true;
  // перрон у путей: герой стоит на платформе
  for (const p of Object.values(PLACES)) {
    walk[p.hero[1]][p.hero[0]] = true;
  }
  return walk;
}

/** Кратчайший путь по клеткам (BFS, 4 направления). Пусто — уже на месте или пути нет. */
export function findPath(walk: boolean[][], from: [number, number], to: [number, number]): [number, number][] {
  const [fx, fy] = from;
  const [tx, ty] = to;
  if (fx === tx && fy === ty) return [];
  const prev = new Map<number, number>();
  const key = (x: number, y: number) => y * MAP_W + x;
  const queue: number[] = [key(fx, fy)];
  prev.set(key(fx, fy), -1);
  const target = key(tx, ty);
  while (queue.length) {
    const cur = queue.shift()!;
    if (cur === target) break;
    const cx = cur % MAP_W;
    const cy = Math.floor(cur / MAP_W);
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= MAP_W || ny >= MAP_H) continue;
      const k = key(nx, ny);
      if (prev.has(k) || (!walk[ny][nx] && k !== target)) continue;
      prev.set(k, cur);
      queue.push(k);
    }
  }
  if (!prev.has(target)) return [];
  const path: [number, number][] = [];
  for (let k = target; k !== -1 && k !== key(fx, fy); k = prev.get(k)!) path.push([k % MAP_W, Math.floor(k / MAP_W)]);
  return path.reverse();
}

/** Что под клеткой: дом или место (для нажатия на карте). */
export function placeAtTile(tx: number, ty: number): string | null {
  for (const b of BUILDINGS) if (tx >= b.x && tx < b.x + b.w && ty >= b.y - 1 && ty < b.y + b.h + 1) return b.id;
  return null;
}
